/**
 * An AreaLight3D's shadow as Godot 4.7.2 keeps it: one paraboloid facing the light's -Z, drawn
 * straight into one slot of the positional shadow atlas (`render_forward_clustered.cpp:2755-2768`).
 * three never draws it. The shadow pass's area step does (`areaShadowPass.ts`).
 * `positionalShadow.md` beside this file has the design.
 */
import * as THREE from 'three';
import { areaLightBounds, areaLightShadowCoverageRadius, areaLightShadowDepthScale, areaLightShadowFar, } from '../../godot/rendering/lights/areaLightShadow.js';
import { confineShadowScissor } from '../sharedAtlasShadow.js';
import { readLightPose, viewToLightPose } from './lightPose.js';
import { drawIntoAtlasSlot, positionalShadowAtlas } from './shadowAtlasTarget.js';
import { readIntensityThroughSlot } from './slotOpacity.js';
/**
 * The camera the paraboloid renders through: the light's pose, and a projection that maps the
 * distance the vertex stage writes into z, from the light to `far`, onto the clip range. Godot
 * writes `distance / z_far * 2 - 1` and renders through an identity projection
 * (`scene_forward_clustered.glsl:503-518`, `renderer_scene_cull.cpp:2661`).
 */
class ParaboloidCamera extends THREE.Camera {
    far = 1;
    updateProjectionMatrix() {
        this.projectionMatrix.set(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 2 / this.far, -1, 0, 0, 0, 1);
        this.projectionMatrixInverse.copy(this.projectionMatrix).invert();
    }
}
const casterBox = new THREE.Box3();
/**
 * The casters the paraboloid renders: those whose world box meets the light's volume, as Godot culls
 * an instance's AABB against the volume's planes (`renderer_scene_cull.cpp:2599-2662`,
 * `dynamic_bvh.h:137-177`). three's own test takes the bounding sphere, which reaches past a corner.
 */
class AreaShadowVolume extends THREE.Frustum {
    /** Sets the planes to the faces of `box`, a box in the space of `pose`. */
    enclose(box, pose) {
        for (let axis = 0; axis < 3; axis++) {
            const [lower, upper] = [this.planes[axis * 2], this.planes[axis * 2 + 1]];
            lower.normal.set(0, 0, 0).setComponent(axis, 1);
            lower.constant = -box.min[axis];
            upper.normal.set(0, 0, 0).setComponent(axis, -1);
            upper.constant = box.max[axis];
        }
        for (const plane of this.planes)
            plane.applyMatrix4(pose);
    }
    intersectsObject(object) {
        casterBox.copy(localBox(object)).applyMatrix4(object.matrixWorld);
        return this.intersectsBox(casterBox);
    }
}
/** The object's own box: an instanced or skinned mesh keeps one, any other mesh its geometry's. */
function localBox(object) {
    const boxed = object;
    if (boxed.boundingBox !== undefined) {
        if (boxed.boundingBox === null)
            boxed.computeBoundingBox();
        return boxed.boundingBox;
    }
    if (boxed.geometry.boundingBox === null)
        boxed.geometry.computeBoundingBox();
    return boxed.geometry.boundingBox;
}
const AreaShadowBase = THREE.LightShadow;
export class AtlasAreaShadow extends AreaShadowBase {
    /** The light's slot in the atlas of the viewport that renders now, or null for none. */
    slot = null;
    opacity = 1;
    /** `area_range`. */
    range = 0;
    /** `area_size`. */
    size = new THREE.Vector2();
    /** The slot's rectangle in units of the slot, as three gives a viewport (r187 `WebGLShadowMap.js:347-352`). */
    slotViewport = new THREE.Vector4(0, 0, 1, 1);
    constructor() {
        super(new ParaboloidCamera());
        this.autoUpdate = false;
        this.map = positionalShadowAtlas();
        this._frustum = new AreaShadowVolume();
    }
    /** Draws into `slot` of the atlas from the next shadow pass on, or into nothing for null. */
    place(slot) {
        this.slot = slot;
        if (slot === null)
            return;
        drawIntoAtlasSlot(this, slot, this.slotViewport);
    }
    /**
     * three calls this before it binds and clears the atlas (r187 `WebGLShadowMap.js:291`). The camera takes
     * the light's pose, the clear stays in the slot, and the casters are those in the light's volume.
     */
    updateMatrices(light) {
        this.fitCamera(light);
        this._frustum.enclose(this.bounds(), this.camera.matrixWorld);
        confineShadowScissor(this, this.slotViewport);
    }
    /**
     * Writes the matrix from the space of `view` into the light's own, which Godot's lookup reads as
     * `shadow_matrix` (4.7.2 `light_storage.cpp:1196-1198`). It takes the pose the map renders from.
     */
    viewToLookup(view, target, light) {
        return viewToLightPose(light, view, target);
    }
    /** Writes the slot as `atlas_rect`: its corner and side in units of the atlas, or null without one. */
    slotRect(target) {
        if (this.slot === null)
            return null;
        const atlasSize = positionalShadowAtlas().width;
        const { x, y, size } = this.slot;
        return target.set(x, y, size, size).divideScalar(atlasSize);
    }
    /** Lets go of the atlas, which other shadows share. */
    dispose() {
        this.map = null;
    }
    /** The light's volume in its own space, which holds every caster the paraboloid renders. */
    bounds() {
        return areaLightBounds(this.range, this.size);
    }
    /** The distance the atlas projects onto the screen to choose the slot. */
    coverageRadius() {
        return areaLightShadowCoverageRadius(this.range, this.size);
    }
    /** The lookup's scale from a distance to the light to the depth the map holds. */
    depthScale() {
        return areaLightShadowDepthScale(this.range, this.size);
    }
    /** Puts the camera at the light's pose, with the far plane its range and size reach. */
    fitCamera(light) {
        readLightPose(light, this.camera.position, this.camera.quaternion);
        this.camera.updateMatrixWorld();
        this.camera.far = areaLightShadowFar(this.range, this.size);
        this.camera.updateProjectionMatrix();
    }
}
readIntensityThroughSlot(AtlasAreaShadow);
export function isAreaShadowLight(light) {
    return light.shadow instanceof AtlasAreaShadow;
}
//# sourceMappingURL=atlasAreaShadow.js.map